import React from 'react';
import { BACKEND_URL } from '../services/api';

const OfficialApplicationPrint = ({ app, student, printedBy = "Admin" }) => {
    if (!app || !student) return null;

    const details = app.additional_details || {};

    const formatDate = (dateString) => {
        if (!dateString) return '....................';
        return new Date(dateString).toLocaleDateString('en-GB');
    };

    // Helper to render checkbox
    const renderCheckbox = (label, checked) => (
        <div className="flex items-center gap-2 mr-6">
            <span className="text-2xl leading-none">{checked ? '☑' : '☐'}</span>
            <span className="text-lg">{label}</span>
        </div>
    );

    // Helper for dotted underline inputs (Balanced spacing)
    const Field = ({ label, value, className = "", flex = true }) => (
        <div className={`flex ${flex ? 'items-end' : 'flex-col'} gap-1.5 ${className}`}>
            {label && <span className="text-lg font-bold whitespace-nowrap">{label}:</span>}
            <div className="flex-1 border-b border-dotted border-black min-h-[1.65rem] text-lg px-2 italic font-medium leading-tight">
                {value || ''}
            </div>
        </div>
    );

    return (
        <div className="form-container bg-white text-black leading-tight mx-auto">
            <style>{`
                @media screen {
                    .form-container {
                        box-shadow: 0 0 10px rgba(0,0,0,0.1);
                        margin-top: 1rem;
                        margin-bottom: 1rem;
                        width: 210mm;
                        min-height: 297mm;
                        padding: 8mm;
                    }
                }
                @media print {
                    body {
                        margin: 0;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        background: white !important;
                        font-family: "Times New Roman", Times, serif !important;
                    }
                    .no-print {
                        display: none;
                    }
                    .form-container {
                        width: 210mm;
                        height: 297mm;
                        padding: 12mm;
                        margin: 0;
                        box-shadow: none;
                        display: block !important;
                        overflow: hidden;
                    }
                    @page {
                        size: A4 portrait;
                        margin: 0;
                    }
                }
                .form-container {
                    font-family: "Times New Roman", Times, serif !important;
                }
            `}</style>

            {/* Header Section - Elegant but Efficient */}
            <div className="flex justify-between items-start mb-4">
                <div className="w-24">
                    <img src={process.env.PUBLIC_URL + '/logo.png'} alt="CSK Logo" className="w-full object-contain" />
                </div>
                
                <div className="text-center flex-1 px-4">
                    <h1 className="text-2xl font-bold tracking-tight">C.S.K Language & Consultancy P. Ltd.</h1>
                    <p className="text-sm">Kalanki-14, Kathmandu, Nepal</p>
                    <div className="mt-1 text-center">
                        <h2 className="text-xl font-bold border-2 border-black inline-block px-8 py-0.5 uppercase">APPLICATION FORM - {app.country}</h2>
                        <p className="text-[10px] mt-0.5 italic font-bold">Processed for: {app.university || app.application_name || 'Visa Application'}</p>
                    </div>
                </div>

                <div className="w-28 flex flex-col items-end text-right">
                    <p className="text-[10px] font-bold mb-0.5">Ref No: APP-{app.id.toString().padStart(4, '0')}</p>
                    <p className="text-[9px] font-bold text-black uppercase tracking-tighter mb-1">Printed By: {printedBy || "Authorized Staff"}</p>
                    <div className="w-24 h-28 border border-black flex items-center justify-center overflow-hidden bg-gray-50">
                        {(details.photo_base64 || student.photo_url) ? (
                            <img 
                                src={details.photo_base64 || (student.photo_url && student.photo_url.startsWith('http') ? student.photo_url : `${BACKEND_URL}${student.photo_url}`)} 
                                alt="Student" 
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-[10px] text-gray-400 text-center px-1 uppercase leading-none">PP Size Photo</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {/* Section 1: Personal Details */}
                <div>
                    <h3 className="text-lg font-bold bg-gray-50 px-3 py-0.5 mb-2 border-l-4 border-black uppercase tracking-wide">1. Personal Information</h3>
                    <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-8">
                            <Field label="Family Name" value={details.family_name} />
                            <Field label="Given Name" value={details.given_name} />
                        </div>
                        <div className="grid grid-cols-2 gap-8">
                            <div className="flex items-end gap-3 h-full pb-1">
                                <span className="text-lg font-bold">Marital Status:</span>
                                {renderCheckbox("Single", details.marital_status === 'Single')}
                                {renderCheckbox("Married", details.marital_status === 'Married')}
                            </div>
                            <Field label="Place of Birth" value={details.place_of_birth} />
                        </div>
                        <div className="grid grid-cols-2 gap-8">
                            <Field label="Email Address" value={student.email} />
                            <Field label="Native Language" value={details.native_language} />
                        </div>
                        <div className="grid grid-cols-12 gap-8">
                            <Field label="Nationality" value={student.nationality} className="col-span-4" />
                            <Field label="Tel 1" value={details.tel_no_1} className="col-span-4" />
                            <Field label="Tel 2" value={details.tel_no_2} className="col-span-4" />
                        </div>
                        <Field label="Current Address" value={details.current_address} />
                    </div>
                </div>

                {/* Section 2: Passport & Travel */}
                <div>
                    <h3 className="text-lg font-bold bg-gray-50 px-3 py-0.5 mb-2 border-l-4 border-black uppercase tracking-wide">2. Passport & Travel</h3>
                    <div className="space-y-2">
                        <div className="grid grid-cols-12 gap-8">
                            <Field label="Passport No" value={student.passport_no || student.passport_number} className="col-span-4" />
                            <Field label="Issuing Authority" value={details.issuing_authority} className="col-span-8" />
                        </div>
                        <div className="grid grid-cols-2 gap-8">
                            <Field label="Issue Date" value={formatDate(details.passport_issue_date)} />
                            <Field label="Expiry Date" value={formatDate(details.passport_expiry_date)} />
                        </div>
                        <Field label="Apply for Visa at" value={details.place_to_apply_for_visa} />
                        <div className="flex items-end gap-6 italic py-1">
                            <span className="text-lg font-bold not-italic">Past Entry:</span>
                            {renderCheckbox("No", !details.past_entry)}
                            {renderCheckbox("Yes", !!details.past_entry)}
                            {details.past_entry && <span className="text-lg border-b border-dotted border-black px-4 ml-4">Times: {details.past_entry_times || 1}</span>}
                        </div>
                    </div>
                </div>

                {/* Section 3: Financial Sponsor */}
                <div>
                    <h3 className="text-lg font-bold bg-gray-50 px-3 py-0.5 mb-2 border-l-4 border-black uppercase tracking-wide">3. Financial Sponsor Details</h3>
                    <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-8">
                            <Field label="Sponsor" value={details.sponsor_name} />
                            <Field label="Relation" value={details.sponsor_relationship} />
                        </div>
                        <div className="grid grid-cols-2 gap-8">
                            <Field label="Occupation" value={details.sponsor_occupation} />
                            <Field label="Office Tel" value={details.sponsor_company_tel} />
                        </div>
                        <Field label="Permanent Address" value={details.sponsor_address} />
                        <Field label="College / Company Name" value={details.sponsor_college || details.sponsor_company} />
                        <div className="grid grid-cols-2 gap-8">
                            <Field label="Father Ph." value={details.sponsor_father_number} />
                            <Field label="Mother Ph." value={details.sponsor_mother_number} />
                        </div>
                    </div>
                </div>

                {/* Section 4: Family Details */}
                <div>
                    <h3 className="text-lg font-bold bg-gray-50 px-3 py-0.5 mb-2 border-l-4 border-black uppercase tracking-wide">4. Family Members Info</h3>
                    <div className="space-y-2">
                        <div className="grid grid-cols-12 gap-8">
                            <Field label="Father's Name" value={details.father_name} className="col-span-8" />
                            <Field label="Date of Birth" value={formatDate(details.father_dob)} className="col-span-4" />
                        </div>
                        <div className="grid grid-cols-12 gap-8">
                            <Field label="Mother's Name" value={details.mother_name} className="col-span-8" />
                            <Field label="Date of Birth" value={formatDate(details.mother_dob)} className="col-span-4" />
                        </div>
                    </div>
                </div>

                {/* Declaration and Signature Section - Balanced */}
                <div className="mt-4 border-2 border-black p-4 py-2">
                    <p className="font-bold text-xl mb-1 uppercase underline underline-offset-4">Declaration</p>
                    <p className="text-[14px] leading-tight italic mb-5">
                        I hereby declare that all provided information is true to the best of my knowledge and belief. I agree to abide by all the rules and regulations of C.S.K Language & Consultancy P. Ltd.
                    </p>
                    
                    <div className="flex justify-between items-end h-20">
                        <div className="w-60 text-center">
                            <div className="border-t border-black mb-1"></div>
                            <p className="text-lg font-bold uppercase">Applicant's Signature</p>
                            <p className="text-[10px]">Date: {new Date().toLocaleDateString()}</p>
                        </div>
                        <div className="w-60 text-center">
                            <div className="border-t border-black mb-1"></div>
                            <p className="text-lg font-bold uppercase">Authorized Stamp</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 text-center text-[10px] text-gray-400 border-t border-gray-100 pt-2 italic">
                CSK-FORM-V2-PREMIUM | Balanced Single Page
            </div>
        </div>
    );
};

export default OfficialApplicationPrint;
